import { useGenericActivityListStore } from 'src/stores/genericActivityList';
import { useIdoActivityHistoryListStore } from 'src/stores/idoActivityHistoryList';
import { RuntimeStoreContext } from 'src/types/runtime/piniaRuntimeContext';

import { ActivityListTypeEnum } from 'src/models/genericActivityList';
import { IdoButton } from 'src/models/ido';
import { StatusEnum } from 'src/models/status/enums';

import { QueryStore } from 'src/util/query/QueryStore';

import type { IdoRelationFilterEntry } from '../types';

const fetchIdoActivityHistoryList = (
	uniqueListIdentifier: string,
	_store: RuntimeStoreContext,
	queryStore: QueryStore,
	queryParameters: {
		activityListType: ActivityListTypeEnum;
		clearResultsBeforeFetch: boolean;
		hiddenSearchTerm: string;
		idoButton: IdoButton;
		searchTerm: string;
	}
) => {
	const idoActivityHistoryListStore = useIdoActivityHistoryListStore();

	if (queryParameters.clearResultsBeforeFetch) {
		if (queryParameters.activityListType === ActivityListTypeEnum.IDO_ACTIVITY_HISTORY_LIST) {
			idoActivityHistoryListStore.clearSubscriptions({
				aggregateId: queryParameters.idoButton.aggregateId,
				queryStore: queryStore,
				uniqueListIdentifier: uniqueListIdentifier,
			});
			idoActivityHistoryListStore.silentSetIdoActivityHistoryList({
				aggregateId: queryParameters.idoButton.aggregateId,
				uniqueListIdentifier: uniqueListIdentifier,
			});
		}
	}

	if (queryParameters.activityListType === ActivityListTypeEnum.IDO_ACTIVITY_HISTORY_LIST) {
		idoActivityHistoryListStore.clearSubscriptions({
			aggregateId: queryParameters.idoButton.aggregateId,
			queryStore: queryStore,
			uniqueListIdentifier: uniqueListIdentifier,
		});
		void idoActivityHistoryListStore.fetchIdoActivityHistoryList({
			aggregateId: queryParameters.idoButton.aggregateId,
			idoButton: queryParameters.idoButton,
			queryStore: queryStore,
			searchTerm: queryParameters.searchTerm,
			uniqueListIdentifier: uniqueListIdentifier,
		});
	}
};

const fetchIdoActivityHistoryListByIdoAndInstanceId = (
	uniqueListIdentifier: string,
	_store: RuntimeStoreContext,
	queryStore: QueryStore,
	queryParameters: {
		activityListType: ActivityListTypeEnum;
		aggregateId: string;
		clearResultsBeforeFetch: boolean;
		hiddenSearchTerm: string;
		idoId: number;
		idoInstanceId: number;
		idoRelations?: Array<IdoRelationFilterEntry>;
		searchTerm: string;
	}
) => {
	const idoActivityHistoryListStore = useIdoActivityHistoryListStore();

	if (queryParameters.clearResultsBeforeFetch) {
		if (
			queryParameters.activityListType === ActivityListTypeEnum.IDO_ACTIVITY_HISTORY_LIST_BY_IDO_AND_INSTANCE_ID
		) {
			idoActivityHistoryListStore.clearSubscriptions({
				aggregateId: queryParameters.aggregateId,
				queryStore: queryStore,
				uniqueListIdentifier: uniqueListIdentifier,
			});

			idoActivityHistoryListStore.silentSetIdoActivityHistoryList({
				aggregateId: queryParameters.aggregateId,
				uniqueListIdentifier: uniqueListIdentifier,
			});
		}
	}

	if (queryParameters.activityListType === ActivityListTypeEnum.IDO_ACTIVITY_HISTORY_LIST_BY_IDO_AND_INSTANCE_ID) {
		idoActivityHistoryListStore.clearSubscriptions({
			aggregateId: queryParameters.aggregateId,
			queryStore: queryStore,
			uniqueListIdentifier: uniqueListIdentifier,
		});

		void idoActivityHistoryListStore.fetchIdoActivityHistoryListByIdoAndInstanceId({
			aggregateId: queryParameters.aggregateId,
			idoId: queryParameters.idoId,
			idoInstanceId: queryParameters.idoInstanceId,
			// exactOptionalPropertyTypes: kein explizites undefined an das optionale Feld reichen.
			...(queryParameters.idoRelations === undefined ? {} : { idoRelations: queryParameters.idoRelations }),
			queryStore: queryStore,
			searchTerm: queryParameters.searchTerm,
			uniqueListIdentifier: uniqueListIdentifier,
		});
	}
};

const unsubscribeFetchIdoActivityHistoryListByIdoAndInstanceId = (
	uniqueListIdentifier: string,
	_store: RuntimeStoreContext,
	queryStore: QueryStore,
	queryParameters: {
		activityListType: ActivityListTypeEnum;
		aggregateId: string;
		hiddenSearchTerm: string;
		idoId: number;
		idoInstanceId: number;
		idoRelations?: Array<IdoRelationFilterEntry>;
		searchTerm: string;
	}
) => {
	const idoActivityHistoryListStore = useIdoActivityHistoryListStore();

	if (queryParameters.activityListType === ActivityListTypeEnum.IDO_ACTIVITY_HISTORY_LIST_BY_IDO_AND_INSTANCE_ID) {
		idoActivityHistoryListStore.registerUnsubscribeFetchIdoActivityHistoryListByIdoAndInstanceId({
			aggregateId: queryParameters.aggregateId,
			idoId: queryParameters.idoId,
			idoInstanceId: queryParameters.idoInstanceId,
			// exactOptionalPropertyTypes: kein explizites undefined an das optionale Feld reichen.
			...(queryParameters.idoRelations === undefined ? {} : { idoRelations: queryParameters.idoRelations }),
			queryStore: queryStore,
			searchTerm: queryParameters.searchTerm,
			uniqueListIdentifier: uniqueListIdentifier,
		});
	}
};

const unsubscribeForIdoActivityHistoryListUpdates = (
	uniqueListIdentifier: string,
	_store: RuntimeStoreContext,
	queryStore: QueryStore,
	queryParameters: {
		activityListType: ActivityListTypeEnum;
		hiddenSearchTerm: string;
		idoButton: IdoButton;
		searchTerm: string;
	}
) => {
	const idoActivityHistoryListStore = useIdoActivityHistoryListStore();

	if (queryParameters.activityListType === ActivityListTypeEnum.IDO) {
		idoActivityHistoryListStore.registerUnsubscribeFetchIdoActivityHistoryList({
			aggregateId: queryParameters.idoButton.aggregateId,
			idoButton: queryParameters.idoButton,
			queryStore: queryStore,
			searchTerm: queryParameters.searchTerm,
			uniqueListIdentifier: uniqueListIdentifier,
		});
	}
};

const deleteTheSelectedIdoHistoryAggregateIds = (
	_store: RuntimeStoreContext,
	_queryStore: QueryStore,
	queryParameters: { aggregateIds: string[]; processId: string }
) => {
	const idoActivityHistoryListStore = useIdoActivityHistoryListStore();
	const genericActivityListStore = useGenericActivityListStore();

	idoActivityHistoryListStore.deleteSelectedAggregateIds({
		aggregateIds: queryParameters.aggregateIds,
	});

	void genericActivityListStore.clearTheSelection({
		genericListIdentifier: 'idoHistory',
	});
};

const setTheStateForTheSelectedIdoHistoryAggregateIds = (
	_store: RuntimeStoreContext,
	_queryStore: QueryStore,
	queryParameters: {
		aggregateIds: string[];
		state: StatusEnum;
	}
) => {
	const idoActivityHistoryListStore = useIdoActivityHistoryListStore();
	const genericActivityListStore = useGenericActivityListStore();

	switch (queryParameters.state) {
		case StatusEnum.UNSEEN:
			idoActivityHistoryListStore.setTheIdoActivityHistoryListStateToUnseen({
				aggregateIds: queryParameters.aggregateIds,
			});
			break;
		case StatusEnum.SEEN:
			idoActivityHistoryListStore.setTheIdoActivityHistoryListStateToSeen({
				aggregateIds: queryParameters.aggregateIds,
			});
			break;
		case StatusEnum.READY:
			idoActivityHistoryListStore.setTheIdoActivityHistoryListStateToReady({
				aggregateIds: queryParameters.aggregateIds,
			});
			break;
		default:
			break;
	}

	void genericActivityListStore.clearTheSelection({
		genericListIdentifier: 'idoHistory',
	});
};

export {
	deleteTheSelectedIdoHistoryAggregateIds,
	fetchIdoActivityHistoryList,
	fetchIdoActivityHistoryListByIdoAndInstanceId,
	setTheStateForTheSelectedIdoHistoryAggregateIds,
	unsubscribeFetchIdoActivityHistoryListByIdoAndInstanceId,
	unsubscribeForIdoActivityHistoryListUpdates,
};
